import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LuPackageOpen,
  LuLayoutDashboard,
  LuTags,
  LuCpu,
  LuChartBar,
  LuUndo2,
  LuUsers,
  LuShield,
  LuFileClock,
  LuComputer,
  LuPlus,
  LuLogOut,
} from "react-icons/lu";
import { RiCouponLine } from "react-icons/ri";
import { MdStorefront } from "react-icons/md";
import { Sun, Moon, Sparkles, Plug } from "lucide-react";

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { api, MainContext } from "../AppContext";
import { openAssistant, useAssistant } from "./assistant/assistant-context";

/**
 * Custom-event channel used by other parts of the app (e.g. the sidebar chip)
 * to programmatically open the global command palette. Mirrors the pattern
 * used by `focusPageSearch` in `useFocusSearchShortcut`.
 */
export const OPEN_COMMAND_MENU_EVENT = "open-command-menu";

export function openCommandMenu() {
  document.dispatchEvent(new CustomEvent(OPEN_COMMAND_MENU_EVENT));
}

const CommandMenu = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { setToken, isDarkMode, setIsDarkMode } = useContext(MainContext);
  const { available: assistantAvailable } = useAssistant();

  useEffect(() => {
    const onKeydown = (event: KeyboardEvent) => {
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen((o) => !o);
      }
    };

    const onOpen = () => setOpen(true);

    document.addEventListener("keydown", onKeydown);
    document.addEventListener(OPEN_COMMAND_MENU_EVENT, onOpen);
    return () => {
      document.removeEventListener("keydown", onKeydown);
      document.removeEventListener(OPEN_COMMAND_MENU_EVENT, onOpen);
    };
  }, []);

  const run = (fn: () => void) => {
    setOpen(false);
    // Defer so the dialog close animation doesn't fight with route changes.
    setTimeout(fn, 0);
  };

  const go = (path: string) => run(() => navigate(path));

  const toggleDarkMode = () => {
    if (isDarkMode) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    }
    setIsDarkMode(!isDarkMode);
  };

  const signOut = () => {
    api.post("/logout").catch(() => undefined);
    api.defaults.headers.common["Authorization"] = "";
    localStorage.removeItem("token");
    setToken(null);
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigation">
          <CommandItem onSelect={() => go("/")}>
            <LuLayoutDashboard />
            <span>Home</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/orders")}>
            <LuPackageOpen />
            <span>Orders</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/products")}>
            <LuCpu />
            <span>Products</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/categories")}>
            <LuTags />
            <span>Categories</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/brands")}>
            <MdStorefront />
            <span>Brands</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/coupons")}>
            <RiCouponLine />
            <span>Coupons</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/users")}>
            <LuUsers />
            <span>Users</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/roles")}>
            <LuShield />
            <span>Roles</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/auditLogs")}>
            <LuFileClock />
            <span>Audit Log</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/stats")}>
            <LuChartBar />
            <span>Analytics</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/maintenance")}>
            <LuUndo2 />
            <span>Maintenance</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/PCGen")}>
            <LuComputer />
            <span>PC Generator</span>
          </CommandItem>
          {assistantAvailable && (
            <CommandItem onSelect={() => go("/settings/mcp")}>
              <Plug />
              <span>MCP access</span>
            </CommandItem>
          )}
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Actions">
          {assistantAvailable && (
            <CommandItem onSelect={() => run(() => openAssistant())}>
              <Sparkles />
              <span>Ask Claude</span>
            </CommandItem>
          )}
          <CommandItem onSelect={() => go("/products/add")}>
            <LuPlus />
            <span>New product</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/coupons/add")}>
            <LuPlus />
            <span>New coupon</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/brands/add")}>
            <LuPlus />
            <span>New brand</span>
          </CommandItem>
          <CommandItem onSelect={() => go("/categories/add")}>
            <LuPlus />
            <span>New category</span>
          </CommandItem>
          <CommandItem onSelect={() => run(toggleDarkMode)}>
            {isDarkMode ? <Sun /> : <Moon />}
            <span>Toggle dark mode</span>
          </CommandItem>
          <CommandItem onSelect={() => run(signOut)}>
            <LuLogOut />
            <span>Sign out</span>
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandMenu;
